import LoadbarComponent from "slotworks/components/preloader/loadbar-component";
import PromptContent, { LoadType } from "slotworks/components/prompt/prompt-content";
import CanvasManager from "slotworks/graphics/canvas/canvas-manager";
import { ButtonEvent } from "slotworks/graphics/elements/button-element";
import Layers from "slotworks/graphics/layers/layers";
import Loader from "slotworks/loader/loader";
import SettingsModel from "slotworks/model/settings/settings-model";
import Contract from "slotworks/utils/contracts/contract";
import { Keyboard } from "slotworks/utils/keyboard";
import Timer from "slotworks/utils/timer";

export default class PromptComponent {

    public static DEFAULT_TIME = 5000;
    public static SPACE_TO_CONTINUE: boolean = true;
    public static ENTER_TO_CONTINUE: boolean = false;

    private keyListener: EventListenerOrEventListenerObject;

    private KEY_UP_EVENT: string = "keyup";

    public showPrompt(prompt: PromptContent, loadStage: number = null): Contract<void> {

        return new Contract<void>((resolve) => {

            prompt.show().then(() => {
                let raceStages = 1;

                const raceHandler = () => {
                    raceStages--;
                    if (raceStages === 0) {
                        prompt.hide().then(() => resolve(null)).execute();
                    }
                };

                let pointerDown = false;

                const onPointerDown = () => {
                    pointerDown = true;
                };

                const onClickToSkip = (event: any) => {
                    if (pointerDown) {
                        onContinue();
                    }
                };

                prompt.enableContinueButton();

                if (loadStage) {
                    raceStages++;
                    if (prompt.getLoadRaceType() === LoadType.DISABLE_BUTTON) {
                        prompt.disableContinueButton();
                    } else if (prompt.getLoadRaceType() === LoadType.HIDE_BUTTON) {
                        prompt.hideContinueButton();
                    }

                    this.loadStage(loadStage).then(() => {
                        prompt.enableContinueButton();

                        if (prompt.getClickToSkip()) {
                            CanvasManager.rootNode.addEventListener(ButtonEvent.POINTER_DOWN.getDOMEventString(), onPointerDown);
                            CanvasManager.rootNode.addEventListener(ButtonEvent.POINTER_UP.getDOMEventString(), onClickToSkip);
                        }

                        raceHandler();
                    });
                } else if (prompt.getClickToSkip()) {
                    CanvasManager.rootNode.addEventListener(ButtonEvent.POINTER_DOWN.getDOMEventString(), onPointerDown);
                    CanvasManager.rootNode.addEventListener(ButtonEvent.POINTER_UP.getDOMEventString(), onClickToSkip);
                }

                if (this.keyListener) {
                    document.removeEventListener(this.KEY_UP_EVENT, this.keyListener);
                }
                this.keyListener = (event: any) => {
                    const spaceKeyPressed = (PromptComponent.SPACE_TO_CONTINUE && event.keyCode === Keyboard.SPACE);
                    const enterKeyPressed = (PromptComponent.ENTER_TO_CONTINUE && event.keyCode === Keyboard.ENTER);
                    const skipKeyPressed = spaceKeyPressed || enterKeyPressed;
                    if (SettingsModel.getSpaceToSpin() && skipKeyPressed) {
                        onContinue();
                    }
                };

                const onContinue = () => {
                    document.removeEventListener(this.KEY_UP_EVENT, this.keyListener);
                    CanvasManager.rootNode.removeEventListener(ButtonEvent.POINTER_DOWN.getDOMEventString(), onPointerDown);
                    CanvasManager.rootNode.removeEventListener(ButtonEvent.POINTER_UP.getDOMEventString(), onClickToSkip);

                    if (prompt.getLoadRaceType() === LoadType.LOADING_SCENE && Layers.Prompts.hasScene("loading")) {
                        Layers.Prompts.setScene("loading").then(() => raceHandler());
                    } else {
                        raceHandler();
                    }
                };

                if (prompt.hasContinueButton()) {
                    document.addEventListener(this.KEY_UP_EVENT, this.keyListener);
                    prompt.onHide.add(() => this.removeKeyListener());
                    prompt.addContinueListener(() => {
                        onContinue();
                    });
                } else {
                    Timer.setTimeout(() => {
                        onContinue();
                    }, prompt.timeOutTime);
                }
            });
        });
    }

    private removeKeyListener() {
        document.removeEventListener(this.KEY_UP_EVENT, this.keyListener);
    }

    private loadStage(stageId: number): Contract<void> {

        const bar = Layers.Prompts.getSprite("loadbar");

        const loadBar = new LoadbarComponent(Loader.getStageFiles(stageId), bar);

        let loadSprite: PIXI.DisplayObject = Layers.Prompts.getSprite("loading_sprite");
        if (!loadSprite) {
            const loadButton = Layers.Prompts.getButton("loading_sprite");
            if (loadButton) {
                loadSprite = loadButton;
            }
        }

        loadBar.addLoadingText(Layers.Prompts.getText("loading_label"), Layers.Prompts.getText("loading_percentage"), loadSprite);

        return new Contract<void>((resolve) => {
            Loader.loadStage(stageId, (stage: number, progress: number, resource: PIXI.loaders.Resource) => {
                loadBar.fileLoaded(resource);
            }).then(() => loadBar.loadComplete().then(() => resolve(null)));
        });
    }
}
